import { useCallback } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError } from "@pontimed/api-client";
import { CirclePause } from "lucide-react";

import { Button } from "@/components/ui/button";
import { apiErrorCode } from "@/lib/api-error-message";

/**
 * Billing refused because the workspace's services are paused
 * (`_docs/service-pause.md`): change-plan, seats, reactivate and checkout
 * answer 409 SERVICES_PAUSED — a plan bought or changed mid-pause would bill
 * at once while the paused plans carry their days. The owner restarts first.
 */

/** DOM id of the "Pause services" card on the billing page (scroll target). */
export const SERVICE_PAUSE_CARD_ID = "service-pause-card";

export function isServicesPausedError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && apiErrorCode(err) === "SERVICES_PAUSED";
}

export function useServicesPausedBillingMessage(): string {
  const { t } = useLingui();
  return t`Your services are paused. Restart them from “Pause services” on your billing page, then try again.`;
}

/**
 * 409 PAUSE_CARRY_ACTIVE: after a resume the paid days of the pause run as a
 * Stripe trial, where a change would be billed 0 € — upgrades, extra seats,
 * interval changes and packs wait for them to end (`carryUntil`). Downgrades,
 * fewer seats and cancelling still go through.
 */
export function isPauseCarryError(err: unknown): boolean {
  return (
    err instanceof ApiError && err.status === 409 && apiErrorCode(err) === "PAUSE_CARRY_ACTIVE"
  );
}

/** The refusal's copy, with the day the carried days end. */
export function usePauseCarryBillingMessage(): (err: unknown) => string {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  return useCallback(
    (err: unknown) => {
      const carryUntil =
        err instanceof ApiError
          ? (err.body as { carryUntil?: unknown } | null)?.carryUntil
          : undefined;
      // As the pause card writes its dates (service-pause-card.tsx useFormatPauseDate).
      const date =
        typeof carryUntil === "string" && !Number.isNaN(Date.parse(carryUntil))
          ? new Date(carryUntil).toLocaleDateString(locale, {
              day: "numeric",
              month: "long",
              year: "numeric",
            })
          : "";
      return t`You have paid days carried over from your pause until ${date}. Plan upgrades, extra seats and billing changes are available after that date.`;
    },
    [t, locale],
  );
}

/** Brings the pause card into view, once the sheet that covered it is gone. */
export function scrollToServicePauseCard(): void {
  const card = document.getElementById(SERVICE_PAUSE_CARD_ID);
  if (!card) return;
  card.scrollIntoView({ behavior: "smooth", block: "center" });
  card.focus({ preventScroll: true });
}

/** The way out of the refusal: closes the sheet and shows the pause card. */
export function GoToServicePauseButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="outline" className="h-11 rounded-lg" onClick={onClick}>
      <CirclePause aria-hidden />
      <Trans>Go to Pause services</Trans>
    </Button>
  );
}
